/**
 * S3 Result Fetching Utilities
 *
 * Used when results are too large for inline Lambda response (>1000 rows).
 * The orchestrator stores results as gzipped TSV in S3 and returns a presigned URL.
 */

// DecompressionStream is a Web API available in modern browsers
// We use window reference to avoid TypeScript errors in CRA
const getDecompressionStream = () => (window as any).DecompressionStream;

export interface S3FetchOptions {
  onProgress?: (message: string) => void;
}

/**
 * Fetches and decompresses gzipped TSV data from S3 presigned URL.
 * Returns data as array of row objects.
 *
 * @param url - S3 presigned URL for gzipped TSV file
 * @param options - Optional callbacks
 * @returns Array of row objects
 */
export async function fetchAndDecompressS3(
  url: string,
  options?: S3FetchOptions
): Promise<Record<string, string>[]> {
  const { onProgress } = options || {};

  onProgress?.('Downloading from S3...');

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download from S3: ${response.status} ${response.statusText}`);
  }

  onProgress?.('Decompressing...');

  const blob = await response.blob();
  const DS = getDecompressionStream();
  const ds = new DS('gzip');
  const decompressedStream = blob.stream().pipeThrough(ds);
  const decompressedText = await new Response(decompressedStream).text();

  onProgress?.('Parsing TSV...');

  // Parse TSV to array of objects
  const lines = decompressedText.trim().split('\n');
  if (lines.length === 0) {
    return [];
  }

  const headers = lines[0].split('\t');
  const data = lines.slice(1).map(line => {
    const values = line.split('\t');
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] || '';
    });
    return row;
  });

  onProgress?.(`Loaded ${data.length.toLocaleString()} rows`);

  return data;
}

/**
 * Check if we need to fetch from S3 based on analysis result.
 * Returns true if s3Only flag is set OR if result is empty but S3 results available.
 */
export function shouldFetchFromS3(result: {
  s3Only?: boolean;
  result?: any[];
  s3Results?: { display_url?: string };
}): boolean {
  // Explicit S3-only mode
  if (result.s3Only) {
    return true;
  }

  // Implicit: empty results but S3 URL available
  if ((!result.result || result.result.length === 0) && result.s3Results?.display_url) {
    return true;
  }

  return false;
}
